﻿if (!customElements.get('m-banner-grid')) {
  customElements.define('m-banner-grid', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.m-banner-grid__track');
      this.items = [...this.track.querySelectorAll('.m-banner-grid__item')];
      this.dots = [...this.querySelectorAll('.m-banner-grid__dot')];
      this.pauseButton = this.querySelector('.m-banner-grid__pause');
      this.compact = matchMedia('(max-width: 1023px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.active = 0;
      this.paused = false;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), { signal }));
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.timer);
        clearTimeout(this.settleTimer);
        this.updateDots();
        this.settleTimer = setTimeout(() => this.settle(), 160);
      }, { passive: true, signal });
      this.track.addEventListener('pointerdown', () => { this.dragging = true; clearTimeout(this.timer); }, { passive: true, signal });
      window.addEventListener('pointerup', () => { this.dragging = false; this.schedule(); }, { signal });
      window.addEventListener('pointercancel', () => { this.dragging = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => clearTimeout(this.timer), { signal });
      this.addEventListener('focusout', () => setTimeout(() => { if (this.isConnected) this.schedule(); }, 0), { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
        this.pauseButton.textContent = this.paused ? '▶' : 'Ⅱ';
        this.schedule();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-banner-index]');
        if (item) this.goTo(Number(item.dataset.bannerIndex));
      }, { signal });
      this.compact.addEventListener('change', () => this.setup(), { signal });
      this.reduced.addEventListener('change', () => this.schedule(), { signal });
      this.observer = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width === this.trackWidth) return;
        this.trackWidth = width;
        if (this.carouselEnabled && this.items[this.active]) this.jump(this.items[this.active]);
      });
      this.observer.observe(this.track);
      this.setup();
    }

    get carouselEnabled() {
      return this.dataset.desktopImages === '1' || this.compact.matches;
    }

    setup() {
      this.dataset.carousel = String(this.carouselEnabled);
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      this.track.querySelectorAll('[data-banner-copy]').forEach(node => node.remove());
      this.looping = this.carouselEnabled && this.items.length > 1;
      if (this.looping) {
        const clone = (item) => {
          const copy = item.cloneNode(true);
          copy.setAttribute('data-banner-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          copy.removeAttribute('data-shopify-editor-block');
          copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          return copy;
        };
        this.track.prepend(clone(this.items.at(-1)));
        this.track.append(clone(this.items[0]));
      }
      if (this.carouselEnabled && this.items[this.active]) this.jump(this.items[this.active]);
      if (!this.carouselEnabled) this.track.scrollLeft = 0;
      this.updateDots();
      this.schedule();
    }

    jump(item) {
      // Identical boundary slides allow an invisible reset, never a reverse scroll.
      this.track.style.scrollSnapType = 'none';
      this.track.scrollBy({ left: item.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: 'instant' });
      cancelAnimationFrame(this.resetFrame);
      this.resetFrame = requestAnimationFrame(() => { this.track.style.scrollSnapType = ''; });
    }

    nearest() {
      const left = this.track.getBoundingClientRect().left;
      return [...this.track.children].reduce((best, item) =>
        !best || Math.abs(item.getBoundingClientRect().left - left) < Math.abs(best.getBoundingClientRect().left - left) ? item : best, null);
    }

    settle() {
      if (!this.carouselEnabled || this.dragging) return;
      const item = this.nearest();
      if (item?.hasAttribute('data-banner-copy')) this.jump(this.items[Number(item.dataset.bannerIndex)]);
      this.updateDots();
      this.schedule();
    }

    goTo(index) {
      if (!this.carouselEnabled || !this.items[index]) return;
      clearTimeout(this.timer);
      const target = this.looping && this.active === this.items.length - 1 && index === 0
        ? this.track.lastElementChild : this.items[index];
      this.track.scrollBy({ left: target.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: this.reduced.matches ? 'instant' : 'smooth' });
      this.schedule();
    }

    updateDots() {
      if (!this.carouselEnabled) return;
      const item = this.nearest();
      this.active = Number(item?.dataset.bannerIndex) || 0;
      this.dots.forEach((dot, index) => {
        if (index === this.active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    schedule() {
      clearTimeout(this.timer);
      const enabled = this.looping && this.dataset.autoplay === 'true' && !this.reduced.matches;
      if (this.pauseButton) this.pauseButton.hidden = !enabled;
      if (!enabled || this.paused || this.dragging || document.hidden || this.matches(':focus-within')) return;
      this.timer = setTimeout(() => this.goTo((this.active + 1) % this.items.length), Math.max(2, Number(this.dataset.interval) || 5) * 1000);
    }

    disconnectedCallback() {
      this.controller.abort();
      this.observer.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.resetFrame);
      this.track.querySelectorAll('[data-banner-copy]').forEach(node => node.remove());
      this.track.style.scrollSnapType = '';
    }
  });
}
